import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { RefreshCw, X, Sparkles } from 'lucide-react';

/**
 * PWA Update Prompt Component
 * Detects service worker updates and automatically applies them.
 * Shows a brief notification to the user when an update was applied.
 */
const PWAUpdatePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateApplied, setUpdateApplied] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let refreshing = false;

    const setupSW = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;

        // If a SW is already waiting, activate it immediately
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New SW installed and waiting — auto-activate it
              setWaitingWorker(newWorker);
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      } catch (err) {
        console.error('[PWA] SW ready error:', err);
      }
    };

    // Listen for controller change (new SW took over) — reload once
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_ACTIVATED') {
        setUpdateApplied(true);
        setTimeout(() => setUpdateApplied(false), 4000);
      }
      if (event.data?.type === 'REFRESH_APP') {
        window.location.reload();
      }
    });

    setupSW();

    // Check for updates every 2 minutes
    const intervalId = setInterval(() => {
      navigator.serviceWorker.ready
        .then((reg) => reg.update())
        .catch(console.error);
    }, 2 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Manual update (fallback if auto-update flow fails)
  const handleUpdate = useCallback(() => {
    if (!waitingWorker) return;
    setIsUpdating(true);
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }, [waitingWorker]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
  }, []);

  // Brief update-applied toast
  if (updateApplied) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-in slide-in-from-bottom-4 duration-300"
        data-testid="pwa-update-applied"
      >
        <div className="bg-tp-accent text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-yellow-300 flex-shrink-0" />
          <p className="text-sm font-medium">Application mise à jour</p>
        </div>
      </div>
    );
  }

  // If there's a stuck waiting worker, show the manual prompt
  if (!showPrompt) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-update-prompt"
    >
      <div className="bg-gradient-to-r from-tp-primary to-tp-primary/90 text-white rounded-xl shadow-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-yellow-300 animate-pulse" />
          <span className="font-medium text-sm">Mise à jour disponible</span>
        </div>

        <div className="px-4 py-3">
          <p className="text-sm text-white/90 mb-3">
            Une nouvelle version est disponible. Mettez à jour pour les dernières fonctionnalités.
          </p>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 bg-white text-tp-primary hover:bg-white/90 font-medium text-sm"
              data-testid="pwa-update-button"
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Mise à jour...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Mettre à jour
                </>
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="pwa-dismiss-button"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdatePrompt;
